// src/components/IjrrGear/SingleProduct/IjrrGearProductDetail.js

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import slugify from 'slugify'; 
import { toast } from 'react-toastify';
import { auth } from '../../../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import AuthModal from '../../Auth/AuthModal';
import ImageEditor from './ImageEditor';
import ProductImagePanel from './ProductImagePanel';
import ProductCustomizationPanel from './ProductCustomizationPanel';
import './IjrrGearProductDetail.css';

const API_BASE_ENDPOINT = `${process.env.REACT_APP_API_BASE_URL}`;

/**
 * IjrrGearProductDetail Component
 * Manages the product customization logic, view transitions, 
 * and high-resolution asset traceability for orders.
 */
export default function IjrrGearProductDetail() {
  const { modelId, colorIndex } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const PROJECT_KEY = 'ijrr_user';

  // --- User State ---
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem(PROJECT_KEY);
    return stored ? JSON.parse(stored) : null;
  });

  // --- Modals and Loading States ---
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); 
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Product Selection ---
  const [product, setProduct] = useState(null);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  
  // --- Design State (Declarative Drivers) ---
  const [previewOverlay, setPreviewOverlay] = useState(null); // Logo URL
  const [activeAssetId, setActiveAssetId] = useState(null);   // DB Asset ID

  // --- Gallery & Editor Setup ---
  const [userAssets, setUserAssets] = useState([]);
  const [hasAutoLoadedMain, setHasAutoLoadedMain] = useState(false);
  const [isAssetUploading, setIsAssetUploading] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);

  // --- View Management ---
  const [activeViewUrl, setActiveViewUrl] = useState(null);
  const [designHistory, setDesignHistory] = useState({}); 

  // --- Helpers ---
  const getProxyUrl = useCallback((url) => {
    if (!url) return null;
    if (url.startsWith('data:')) return url; 
    return `${API_BASE_ENDPOINT}/customer-assets/download-proxy?url=${encodeURIComponent(url)}`;
  }, []);

  // --- Auth Observer ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, firebaseUser => {
      if (firebaseUser) {
        const formatted = { uid: firebaseUser.uid, email: firebaseUser.email, displayName: firebaseUser.displayName };
        setUser(formatted);
        localStorage.setItem(PROJECT_KEY, JSON.stringify(formatted));
      } else {
        setUser(null); 
        setUserAssets([]); 
        setHasAutoLoadedMain(false); 
        setActiveAssetId(null);
      }
    });
    return unsubscribe;
  }, []);

  // --- Fetch Gallery Assets ---
  useEffect(() => {
    const fetchAssets = async () => {
        if (!user?.uid) return;
        try {
            const response = await fetch(`${API_BASE_ENDPOINT}/customer-assets/list`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ firebase_uid: user.uid })
            });
            if (response.ok) {
                const data = await response.json();
                setUserAssets(data.assets || []);
            }
        } catch (error) { 
            console.error("Failed to fetch customer assets:", error); 
        }
    };
    fetchAssets();
  }, [user]);

  /**
   * handleGalleryAssetSelect
   * Updates state only. Duplication is avoided because ImageEditor 
   * reacts to these prop changes in a single useEffect cycle.
   */
  const handleGalleryAssetSelect = (asset) => {
      const proxyUrl = getProxyUrl(asset.url);
      setPreviewOverlay(proxyUrl);
      if (asset.id && !asset.isLocal) setActiveAssetId(asset.id);
      else setActiveAssetId(null); 
  };

  // --- Auto-load main asset on first load ---
  useEffect(() => {
    if (userAssets.length > 0 && isEditorReady && !hasAutoLoadedMain) {
        const mainAsset = userAssets.find(asset => asset.is_main);
        if (mainAsset) handleGalleryAssetSelect(mainAsset);
        setHasAutoLoadedMain(true);
    }
  }, [userAssets, isEditorReady, hasAutoLoadedMain]);

  // --- Fetch Product and Inventory ---
  useEffect(() => {
    async function fetchInventory() {
      try {
        const response = await fetch(`${API_BASE_ENDPOINT}/public-inventory/${modelId}`);
        if (!response.ok) throw new Error('Product fetch failed');
        const data = await response.json();
        const found = data.product; 
        if (found) {
          const idx = parseInt(colorIndex, 10);
          const safeIndex = !isNaN(idx) && idx >= 0 && idx < found.inventories.length ? idx : 0;
          setSelectedColorIndex(safeIndex);
          setProduct(found);
          const variant = found.inventories[safeIndex];
          const frontUrl = variant.thumbnail.replace('/thumbs/', '/');
          setActiveViewUrl(getProxyUrl(frontUrl));
          const sizes = variant.sizes || {};
          setSelectedSize(Object.keys(sizes)[0] || '');
        }
      } catch (error) { 
          console.error('Error fetching product details:', error); 
      } finally { 
          setInitialLoading(false); 
      }
    }
    fetchInventory();
  }, [modelId, colorIndex, getProxyUrl]);

  useEffect(() => {
    if (product?.is_customizable) setTimeout(() => setIsEditorReady(true), 100);
  }, [product]);

  /**
   * transitionCanvasState
   * Manages the transition between different garment views.
   * It saves the current canvas JSON before loading the next one.
   */
  const transitionCanvasState = (nextViewUrl) => {
    if (!editorRef.current) return;
    
    // 1. Save current design to history
    const currentSnapshot = editorRef.current.getDesignJSON();
    setDesignHistory(prev => ({ ...prev, [activeViewUrl]: currentSnapshot }));

    // 2. Update background
    const proxiedNextUrl = getProxyUrl(nextViewUrl);
    setActiveViewUrl(proxiedNextUrl);

    // 3. Restore or auto-apply design
    setTimeout(() => {
        const hasHistory = designHistory.hasOwnProperty(proxiedNextUrl);
        const savedDesign = hasHistory ? designHistory[proxiedNextUrl] : null;
        
        if (hasHistory) {
            editorRef.current?.loadDesignJSON(savedDesign);
        } else if (previewOverlay) {
            editorRef.current?.addOverlayImage(previewOverlay, activeAssetId);
        }
    }, 200);
  };

  const handleViewChange = (nextViewUrl) => {
      if (!product.is_customizable) {
          setActiveViewUrl(getProxyUrl(nextViewUrl));
          return;
      }
      transitionCanvasState(nextViewUrl);
  };

  const handleColorClick = index => {
    const nextVariant = product.inventories[index];
    const nextFrontUrl = nextVariant.thumbnail.replace('/thumbs/', '/');
    if (product.is_customizable) transitionCanvasState(nextFrontUrl);
    else setActiveViewUrl(getProxyUrl(nextFrontUrl));
    setSelectedColorIndex(index);
    const sizes = nextVariant.sizes || {};
    setSelectedSize(Object.keys(sizes)[0] || '');
    navigate(`/ijrr-gear/${modelId}/${slugify(product.name, { lower: true })}/${index}`);
  };

  /**
   * proceedToAddToCart
   * Exports only the views belonging to the CURRENT color.
   */
  const proceedToAddToCart = async () => {
    if (!editorRef.current && product.is_customizable) return;

    setIsSubmitting(true); 
    try {
      const formData = new FormData();
      formData.append('user_id', user.uid);
      formData.append('user_contact[email]', user.email);
      formData.append('product_details[product_name]', product.name);
      formData.append('product_details[product_id]', product.model_id);
      formData.append('product_details[selected_color]', product.inventories[selectedColorIndex].color);
      formData.append('product_details[selected_size]', selectedSize);
      formData.append('product_details[quantity]', selectedQuantity);
      formData.append('product_details[order_status]', 'pending');

      if (product.is_customizable) {
          const currentSnapshot = editorRef.current.getDesignJSON();
          // Merged history including current live view
          const fullHistory = { ...designHistory, [activeViewUrl]: currentSnapshot };

          // --- FIX: Logic to filter only current variant views ---
          const inventory = product.inventories[selectedColorIndex];
          const frontUrl = getProxyUrl(inventory.thumbnail.replace('/thumbs/', '/'));
          
          // List of valid URLs for the CURRENT selected color
          const validUrlsForCurrentColor = [
              frontUrl, 
              ...inventory.additional_views.map(v => getProxyUrl(v.url))
          ];

          const viewLabelMap = { [frontUrl]: 'Front' };
          inventory.additional_views.forEach(v => {
              viewLabelMap[getProxyUrl(v.url)] = v.label;
          });

          let designIndex = 0;
          
          // We iterate ONLY over valid URLs for the selected variant
          for (const url of validUrlsForCurrentColor) {
              const objects = fullHistory[url];
              
              // Only process if there is design data for this valid URL
              if (objects && objects.length > 0) {
                  const imageWithAsset = objects.find(obj => obj.type === 'image' && obj.assetId);
                  const viewAssetId = imageWithAsset ? imageWithAsset.assetId : null;

                  await editorRef.current.prepareView(url, objects);
                  const blob = await editorRef.current.exportCanvasAsPNG();
                  const label = viewLabelMap[url] || 'Custom View';

                  formData.append(`designs[${designIndex}][label]`, label);
                  formData.append(`designs[${designIndex}][preview]`, blob, `${label}.png`);
                  formData.append(`designs[${designIndex}][data]`, JSON.stringify(objects));
                  
                  if (viewAssetId) {
                      formData.append(`designs[${designIndex}][customer_asset_id]`, viewAssetId);
                  }
                  
                  designIndex++;
              }
          }

          // Fallback if no specific designs were found for current color
          if (designIndex === 0) {
              const response = await fetch(activeViewUrl);
              const blob = await response.blob();
              formData.append('product_preview', blob, 'default_base.jpg');
          }

          // Restore the editor to its original state before finishing
          await editorRef.current.prepareView(activeViewUrl, currentSnapshot);
      } else {
          const response = await fetch(activeViewUrl);
          const blob = await response.blob();
          formData.append('product_preview', blob, 'preview.jpg');
      }

      const response = await fetch(`${API_BASE_ENDPOINT}/orders`, { 
          method: 'POST', 
          body: formData 
      });
      if (!response.ok) throw new Error('API request failed');
      
      toast.success('Added to cart successfully!');
      setIsConfirmationModalOpen(false);
      setTimeout(() => navigate('/shopping-cart'), 1500);

    } catch (error) {
        console.error("Order error:", error);
        toast.error('Failed to place order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (initialLoading) return <div className="container text-center py-5"><h3>Loading Product...</h3></div>;
  if (!product) return <div className="container text-center py-5"><h3>Product not found.</h3></div>;

  return (
    <section className="space-py-80 product-detail-page product-detail-page-space">
      {isSubmitting && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <div className="spinner-border text-primary" role="status"></div>
              <h4 className="mt-3">Processing designs for {product.inventories[selectedColorIndex].color}...</h4>
          </div>
      )}

      <div className="custom-container">
        <div className="row">
          <div className="col-12 mobile-only-filter-toggle">
            <div className="card mb-20">
                <div className="card-body">
                    <div className="grid-wrap-header">
                        <div className="d-xl-none breadcrumb-mobile">
                            <a href="/ijrr-gear" className="text-dark">All</a>
                            <span className="mx-1">/</span>
                            <span className="text-muted">{product.categoryName}</span>
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {product.is_customizable ? (
            <div className="col-md-7 cdx-xl-45 product-editor-wrap">
              {isEditorReady && (
                <ImageEditor
                  ref={editorRef}
                  selectedImageUrl={activeViewUrl}
                  overlayImageUrl={previewOverlay}
                  overlayAssetId={activeAssetId}
                  onOverlayCleared={() => {}}
                />
              )}
            </div>
          ) : ( <ProductImagePanel imageUrl={activeViewUrl} productName={product.name} /> )}

          <ProductCustomizationPanel
            product={product}
            selectedColorIndex={selectedColorIndex}
            handleColorClick={handleColorClick}
            setPreviewOverlay={setPreviewOverlay}
            handleAddOverlayImage={(img, id) => {
                setPreviewOverlay(img);
                setActiveAssetId(id);
            }}
            onAddToCart={() => setIsConfirmationModalOpen(true)}
            activeViewUrl={activeViewUrl}
            onViewChange={handleViewChange}
            getProxyUrl={getProxyUrl}
            userAssets={userAssets}
            onAssetSelect={handleGalleryAssetSelect}
            isUserLoggedIn={!!user}
            activeAssetUrl={previewOverlay}
            onUpload={() => setIsAssetUploading(true)}
            isUploading={isAssetUploading}
            selectedSize={selectedSize}
            onSizeChange={setSelectedSize}
            selectedQuantity={selectedQuantity}
            onQuantityChange={setSelectedQuantity}
          />
        </div>
      </div>
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} onLoginSuccess={u => setUser(u)} />
      {isConfirmationModalOpen && (
         <div className="error-modal" onClick={() => setIsConfirmationModalOpen(false)}>
            <div className="error-content" onClick={e => e.stopPropagation()}>
                <button className="close-btn" onClick={() => setIsConfirmationModalOpen(false)}>×</button>
                <h3>Confirm Order</h3>
                <p>Only the designs for the current color (<b>{product.inventories[selectedColorIndex].color}</b>) will be submitted.</p>
                <div className="error-buttons">
                    <button className="btn-secondary" onClick={() => setIsConfirmationModalOpen(false)} disabled={isSubmitting}>Cancel</button>
                    <button className="btn-primary" onClick={proceedToAddToCart} disabled={isSubmitting}>
                        {isSubmitting ? 'Confirming...' : 'Confirm'}
                    </button>
                </div>
            </div>
         </div>
      )}
    </section>
  );
}